import React, { useEffect, useRef } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { useToasts } from 'ui/toast';

// The codes axios assigns to transport failures: the request went out and no
// response came back. Excludes cancellations (deliberate) and pre-dispatch
// config errors (ERR_INVALID_URL etc. — bugs, not connectivity).
const TRANSPORT_FAILURE_CODES = ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'];

// Errors can reach react-query either as raw axios errors or wrapped in
// HTTPError (apis/HTTPErrors.js), which keeps the axios error on
// `originalError`. A network error must be positively identified by its
// transport-failure code. Client-side throws from a mutationFn (validation,
// parsing, bugs) are not axios errors at all, but retrying them is useless
// and "check your internet connection" would be misleading, so anything not
// positively identified is treated as non-network.
export function isNetworkError(error) {
  const cause = error?.isAxiosError ? error : error?.originalError;
  return !!(
    cause?.isAxiosError &&
    !cause.response &&
    TRANSPORT_FAILURE_CODES.includes(cause.code)
  );
}

export const MUTATION_RETRY_LIMIT = 3;

export function mutationRetry(failureCount, error) {
  // Only retry network-level failures: an HTTP response means the server made
  // a decision (retrying a 4xx is pointless, retrying a 5xx risks
  // double-creating non-idempotent POSTs).
  return isNetworkError(error) && failureCount < MUTATION_RETRY_LIMIT;
}

export function mutationRetryDelay(attemptIndex) {
  return Math.min(2000 * 2 ** attemptIndex, 15000); // 2s, 4s, 8s
}

export function createQueryClient() {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // Data is considered fresh for 5 minutes
        retry: 2,                 // Retry failed requests 2 times
        refetchOnWindowFocus: false, // Disable automatic refetch on window focus
      },
      mutations: {
        // The default networkMode ('online') pauses mutations while the
        // browser is offline: no request, no error, and optimistic updates
        // look saved indefinitely. 'always' attempts immediately so failures
        // surface through retry/onError and optimistic rollbacks run.
        networkMode: 'always',
        retry: mutationRetry,
        retryDelay: mutationRetryDelay,
      },
    },
  });

  // Codes are read by cache-only hooks (useCode/useCodesById in
  // hooks/codes/queries.js) that are deliberately not query observers, so
  // nothing pins their entries against garbage collection: seeded detail
  // entries would vanish after the default 5-minute gcTime and code chips
  // would silently disappear from rendered excerpts. Keep codes queries
  // (lists and details) around for 30 minutes after their last observer —
  // the retention the old observer-based readers provided.
  client.setQueryDefaults(['codes'], { gcTime: 30 * 60 * 1000 });

  // Excerpt detail entries are the SOLE store for excerpt objects under
  // the ids-only transcript model (the transcript entry holds only
  // excerpt_ids) — an evicted entry means a silently missing excerpt:
  // blank highlights, truncated agreement overlays, wrong intercoder
  // scores. Session-persistent entries make that whole failure class
  // structurally impossible, at a memory cost strictly below the old
  // model's (which kept a full duplicate excerpts array inside every
  // cached transcript). A member with no entry is therefore an invariant
  // violation, reported to Sentry (reportMissingExcerptDetails) rather
  // than refetched.
  client.setQueryDefaults(['excerpts', 'detail'], { gcTime: Infinity });

  return client;
}

export function shouldToastMutationError(event) {
  return (
    event?.type === 'updated' &&
    event.action?.type === 'error' &&
    isNetworkError(event.action.error)
  );
}

const SAVE_ERROR_TOAST_THROTTLE_MS = 10000;
const SAVE_ERROR_MESSAGE =
  "Your change couldn't be saved. Check your internet connection and try again.";

// Mutations roll back their optimistic updates on error, but most have no
// user-facing message. This surfaces connection-level save failures globally
// (throttled, so a burst of failed saves shows one toast, not one each).
export function MutationErrorToaster({ queryClient }) {
  const { addToast } = useToasts();
  const lastToastAtRef = useRef(0);

  useEffect(() => {
    return queryClient.getMutationCache().subscribe((event) => {
      if (!shouldToastMutationError(event)) return;

      const now = Date.now();
      if (now - lastToastAtRef.current < SAVE_ERROR_TOAST_THROTTLE_MS) return;
      lastToastAtRef.current = now;

      addToast(SAVE_ERROR_MESSAGE, { appearance: 'error' });
    });
  }, [queryClient, addToast]);

  return null;
}

export default createQueryClient;
